import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Box, Grid, GridItem, Text, Button, IconButton, Icon, Input, InputGroup, InputLeftElement,
    Spinner, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    ModalFooter, useColorModeValue,
} from "@chakra-ui/react";
import { MdPersonAdd, MdSearch } from "react-icons/md";
import FeeDiagrams from "../../profileData/feeDiagrams";
import ProfileCard from "./ProfileCard";

/**
 * Admin management page with profile, admin controls, and fee diagrams.
 * @returns {React.ReactElement}
 */
function User() {
    const [right, setRight] = useState(1);
    const [show, setShow] = useState(false);
    const [profile, setProfile] = useState({});
    const [smShow, setSmShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userToAdmin, setUserToAdmin] = useState({});
    const [searchResult, setSearchResult] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    /** Fetches the current admin profile from the API. */
    const checkAdminProfile = useCallback(async () => {
        const data = (await axios.get('profiles/my_profile')).data;
        setRight(data['right']);
        setProfile(data['my_profile']);
    }, []);

    /** @param {Object} e - Input change event */
    const searchUser = async (e) => {
        const str = e.target.value;
        if (!str) return;
        setSearchLoading(true);
        setSearchResult((await axios.get('/admin/users/search/' + str)).data);
        setSearchLoading(false);
    };

    /** Promotes the selected user to admin role. */
    const addNewAdmin = () => {
        setLoading(true);
        axios.put("/admin/users/switch_right/" + userToAdmin.id.toString() + "/1")
            .then(() => { setShow(false); setSmShow(false); setLoading(false); toast.success("Nouvel admin ajouté avec succès"); });
    };

    /** @param {Object} value - User object to promote */
    const alertNewAdmin = (value) => { setUserToAdmin(value); setSmShow(true); };

    useEffect(() => { checkAdminProfile(); }, [checkAdminProfile]);

    return (
        <Box p="4px" pb="40px" maxH="100vh" overflow="auto">
            <AddAdminDialog show={show} setShow={setShow} searchUser={searchUser}
                searchLoading={searchLoading} searchResult={searchResult} onSelect={alertNewAdmin} />
            <ConfirmAdminDialog smShow={smShow} setSmShow={setSmShow}
                userName={userToAdmin.name} loading={loading} onConfirm={addNewAdmin} />
            <UserPageGrid profile={profile} right={right} onAddClick={() => setShow(true)} />
        </Box>
    );
}

/**
 * Renders the two-column grid with profile card and fee diagrams.
 * @param {object} props
 * @param {object} props.profile - Admin profile data
 * @param {number} props.right - Admin right level
 * @param {function} props.onAddClick - Handler to open add-admin dialog
 * @returns {React.ReactElement}
 */
function UserPageGrid({ profile, right, onAddClick }) {
    return (
        <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap="24px">
            <GridItem colSpan={{ base: 12, md: 4 }}>
                <ProfileCard profile={profile} right={right} onAddClick={onAddClick} />
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 8 }}>
                <FeeDiagrams />
            </GridItem>
        </Grid>
    );
}

/**
 * Dialog for searching and selecting a user to promote to admin.
 * @param {Object} props - { show, setShow, searchUser, searchLoading, searchResult, onSelect }
 * @returns {React.ReactElement}
 */
function AddAdminDialog({ show, setShow, searchUser, searchLoading, searchResult, onSelect }) {
    return (
        <Modal isOpen={show} onClose={() => setShow(false)} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader><Flex align="center" gap="8px"><Icon as={MdPersonAdd} />Ajouter un Admin</Flex></ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <InputGroup mb="16px">
                        <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                        <Input placeholder="Chercher le nouveau admin" onChange={searchUser} />
                    </InputGroup>
                    <SearchResultList loading={searchLoading} results={searchResult} onSelect={onSelect} />
                </ModalBody>
                <ModalFooter><Button variant="ghost" onClick={() => setShow(false)}>Fermer</Button></ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/**
 * Renders the search results list with user items.
 * @param {Object} props - { loading, results, onSelect }
 * @returns {React.ReactElement}
 */
function SearchResultList({ loading, results, onSelect }) {
    if (loading) return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    if (!results.length) return <Text color="secondaryGray.600" textAlign="center" py="32px">Pas de résultat à afficher</Text>;
    return (
        <Box maxH="300px" overflowY="auto">
            {results.map((value, index) => (<SearchResultItem key={index} value={value} onSelect={onSelect} />))}
        </Box>
    );
}

/**
 * Renders a single search result item with name, email, and add button.
 * @param {object} props - { value, onSelect }
 * @returns {React.ReactElement}
 */
function SearchResultItem({ value, onSelect }) {
    const itemBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center" justify="space-between" p="12px" mb="8px" borderRadius="10px" bg={itemBg}>
            <Box>
                <Text color={nameColor} fontWeight="600">{value.name}</Text>
                <Text color="secondaryGray.600" fontSize="0.85rem">{value.email}</Text>
            </Box>
            <IconButton aria-label="Ajouter" onClick={() => onSelect(value)} variant="ghost"
                colorScheme="green" icon={<Icon as={MdPersonAdd} />} />
        </Flex>
    );
}

/**
 * Confirmation dialog for promoting a user to admin.
 * @param {Object} props - { smShow, setSmShow, userName, loading, onConfirm }
 * @returns {React.ReactElement}
 */
function ConfirmAdminDialog({ smShow, setSmShow, userName, loading, onConfirm }) {
    return (
        <Modal isOpen={smShow} onClose={() => setSmShow(false)} size="sm" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalBody pt="24px" textAlign="center">
                    <Text fontSize="1.1rem">Ajouter {userName} comme admin ?</Text>
                </ModalBody>
                <ModalFooter justifyContent="center" gap="8px">
                    <Button variant="ghost" colorScheme="red" onClick={() => setSmShow(false)}>Non</Button>
                    <Button colorScheme="green" onClick={onConfirm} isLoading={loading}>Oui</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default User;
